import React from 'react';
import chroma from 'chroma-js';
import styled from 'styled-components/macro';
import { Text } from '../../typography';
import { SPACINGS } from '../../utils';

const NavbarStyled = styled.div`
  /* position: unset; */
  /* overflow: hidden; */
  background-color: ${(p) => chroma(p.theme.colors.white).alpha(0.5).hex()};
  backdrop-filter: blur(5px);
  display: flex;
  align-items: stretch;
  justify-content: center;
  box-shadow: ${(p) => p.theme.boxShadows.xs};
  border-radius: 4px;
`;

const ItemStyled = styled.button<{ isSelected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  flex-grow: 1;
  background-color: ${(p) =>
    p.isSelected ? p.theme.colors.primary : 'transparent'};
  padding: ${SPACINGS[3]} ${SPACINGS[2]};
  border: none;
  border-radius: 0;
  cursor: pointer;
  transition: background-color 0.2s;

  &:first-child {
    border-radius: 4px 0 0 4px;
  }
  &:last-child {
    border-radius: 0 4px 4px 0;
  }

  &:hover {
    outline: none;
    background-color: ${(p) => p.theme.colors.primary20b};
  }

  &:focus,
  &:active {
    outline: none;
    background-color: ${(p) => p.theme.colors.primary};
  }
`;

const MarkerContainer = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%) translateY(-100%);
`;

type ItemWithParams = {
  disabled?: boolean;
  hidden?: boolean;
  style?: React.CSSProperties;
};
type MobileNavbarProps<Item> = {
  items: (Item & ItemWithParams)[];
  onChange: (items: Item & ItemWithParams) => void;
  valueAccessor: (item: (Item & ItemWithParams) | undefined) => string;
  labelAccessor: (
    item: (Item & ItemWithParams) | undefined
  ) => string | React.ReactNode;
  selectedItem?: Item & ItemWithParams;
  defaultItem?: Item & ItemWithParams;
  markerItem?: Item & ItemWithParams;
  marker?: React.ReactNode;
  style?: React.CSSProperties;
};
function MobileNavbar<Item>(props: MobileNavbarProps<Item>) {
  const {
    items,
    labelAccessor,
    valueAccessor,
    defaultItem,
    selectedItem: selectedItemBase,
    onChange,
    markerItem,
    marker,
    style,
  } = props;

  const isControlled = 'selectedItem' in props;
  const [selectedItem, setSelectedItem] = React.useState(
    isControlled ? selectedItemBase : defaultItem
  );

  React.useEffect(() => {
    if (isControlled) setSelectedItem(selectedItemBase);
  }, [isControlled, selectedItemBase]);

  const handleChange = React.useCallback(
    (item: Item) => {
      if (valueAccessor(item) !== valueAccessor(selectedItem)) {
        setSelectedItem(item);
        onChange(item);
      }
    },
    [onChange, selectedItem, valueAccessor]
  );

  return (
    <NavbarStyled style={style}>
      {items.map((item, i) => (
        <ItemStyled
          data-testid={`mobile-navbar-${valueAccessor(item)}`}
          key={valueAccessor(item)}
          isSelected={valueAccessor(item) === valueAccessor(selectedItem)}
          onClick={() => handleChange(item)}
        >
          {marker && valueAccessor(item) === valueAccessor(markerItem) && (
            <MarkerContainer>{marker}</MarkerContainer>
          )}
          {['string', 'number'].includes(typeof labelAccessor(item)) ? (
            <Text margin="none">{labelAccessor(item)}</Text>
          ) : (
            labelAccessor(item)
          )}
        </ItemStyled>
      ))}
    </NavbarStyled>
  );
}

export default MobileNavbar;
